import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import { motion } from "framer-motion";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination";
import { hasPermission } from "../config/permissions";
import { useUser } from "../config/userProvider";

const NewApplications = () => {
  let { userData } = useUser();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [paginationData, setPaginationData] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${yyyy}-${mm}-${dd}`;

  const navigate = useNavigate();

  useEffect(() => {
    fetchApplications();
  }, [currentPage, itemsPerPage]);

  const fetchApplications = () => {
    setLoading(true);
    fetch(
      `${apiurl}/api/application/filter?page=${currentPage}&limit=${itemsPerPage}&startDate=${formattedDate}&endDate=${formattedDate}`
    )
      .then((res) => res.json())
      .then((data) => {
        setApplications(data.applications || []);
        setTotalItems(data.pagination?.totalItems || 0);
        setTotalPages(data.totalPages || 1);
        setLoading(false);
        setPaginationData(data?.pagination);
        setSelectAll(false);
        setSelectedApplications([]);
      })
      .catch((err) => {
        console.error("Error fetching applications:", err);
        setLoading(false);
      });
  };

  // ✅ Search filter (name/email only)
  const filteredApps = applications.filter((app) => {
    const nameMatch =
      app?.candidateSnapshot?.firstName
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      app?.candidateSnapshot?.lastName
        ?.toLowerCase()
        .includes(search.toLowerCase());
    const emailMatch = app?.candidateSnapshot?.email
      ?.toLowerCase()
      .includes(search.toLowerCase());
    return nameMatch || emailMatch;
  });

  // ✅ Status wise counts
  const statusCounts = applications.reduce((acc, app) => {
    const status = app.applicationStatus || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const listVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: { delay: index * 0.05, duration: 0.3 },
    }),
  };

  const handleLimitChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleCheckboxChange = (appId) => {
    setSelectedApplications((prevSelected) =>
      prevSelected.includes(appId)
        ? prevSelected.filter((id) => id !== appId)
        : [...prevSelected, appId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(applications.map((app) => app._id));
    }
    setSelectAll(!selectAll);
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-2 bg-gray-100 min-h-screen">
          {/* 🔍 Search + Stats */}
          <div className="flex flex-col gap-4 mb-6">
            {/* Top bar */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <input
                type="text"
                placeholder="Search by name or email"
                className="p-2 border rounded w-full md:w-1/3"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <div className="flex items-center gap-4">
                <span className="font-semibold text-gray-700">
                  Total: {totalItems}
                </span>
                <select
                  className="p-2 border rounded"
                  value={itemsPerPage}
                  onChange={handleLimitChange}
                >
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                </select>
              </div>
            </div>

            {/* Status wise summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {Object.entries(statusCounts).map(([status, count]) => (
                <div
                  key={status}
                  className="bg-white rounded-lg shadow p-3 text-center"
                >
                  <p className="text-sm font-medium text-gray-500">{status}</p>
                  <p className="text-lg font-bold text-gray-800">{count}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <SuwasthiLoader />
          ) : (
            <div className="bg-white shadow-md rounded-lg p-4 overflow-x-auto overflow-y-auto ">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="p-3">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="p-3 text-left">Application ID</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-gray-500">
                        No applications found
                      </td>
                    </tr>
                  ) : (
                    filteredApps.map((app, index) => (
                      <motion.tr
                        key={app._id}
                        className={`border-b ${
                          !app.isRead ? "bg-yellow-100" : ""
                        }`}
                        variants={listVariants}
                        initial="hidden"
                        animate="visible"
                        custom={index}
                      >
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedApplications.includes(app._id)}
                            onChange={() => handleCheckboxChange(app._id)}
                          />
                        </td>
                        <td className="p-3 flex items-center gap-2">
                          {app.isNewApplicant && (
                            <motion.span
                              className="bg-green-500 text-white px-2 py-1 text-xs rounded"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 200 }}
                            >
                              New
                            </motion.span>
                          )}
                          {app?.id}
                        </td>
                        <td className="p-3">
                          {app?.candidateSnapshot?.firstName}{" "}
                          {app?.candidateSnapshot?.lastName}
                        </td>
                        <td className="p-3">{app.candidateSnapshot?.email}</td>
                        <td className="p-3">{app.applicationStatus}</td>
                        <td className="p-3 flex gap-2">
                          {hasPermission(
                            userData.permissions,
                            "application",
                            "read"
                          ) && (
                            <Link
                              className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition"
                              to={`/applications/${app._id}`}
                            >
                              View
                            </Link>
                          )}
                          {hasPermission(
                            userData.permissions,
                            "application",
                            "write"
                          ) && (
                            <Link
                              className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition"
                              to={`/applications/${app._id}/manage`}
                            >
                              Manage
                            </Link>
                          )}
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </Container>
    </Layout>
  );
};

export default NewApplications;